import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import head from 'lodash/head';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import {
  blobToDataUrl,
  isEmptyString,
} from '@populateui/utils';
import BuilderLayout from 'views/EMR/Settings/PdfTemplateBuilder/components/BuilderLayout';
import PdfDocumentViewer from 'views/EMR/Settings/PdfTemplateBuilder/components/PdfDocumentViewer';
import PropertiesPanel from 'views/EMR/Settings/PdfTemplateBuilder/components/PropertiesPanel';
import TopBar from 'views/EMR/Settings/PdfTemplateBuilder/components/TopBar';
import Dialog from 'pages/Dashboard/components/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from 'core/icons/delete';
import Skeleton from 'core/components/Skeleton';
import {
  PdfTemplateField,
  PdfPageRect,
  PdfTemplateBuilderLocationState,
  PdfTemplateSummary,
} from 'views/EMR/Settings/PdfTemplateBuilder/types';
import {
  useGetFormBuilder,
  useGetFormPreviewPdf,
  useReplaceFormPdf,
  useSaveFormFields,
  useFormBuilderState,
  useDeleteForm,
  useGetFormTemplateSettings,
  useGetFormCategoryRules,
} from 'views/EMR/Settings/PdfTemplateBuilder/hooks';
import {
  normalizeFormFields,
  serializeFormFields,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/formFields';
import {
  getFieldHistoryShortcutAction,
  isEditableKeyboardTarget,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/keyboardShortcuts';
import {
  getDefaultFieldType,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/fieldTypes';
import {
  countFieldsNeedingCoercion,
  findFormCategoryRule,
  getAssigneeLabel,
  getCoercionTargetAssignee,
  getDefaultAssigneeForNewField,
  inferFormAssigneeSelection,
  isMixedAssigneeMode as getIsMixedAssigneeMode,
  normalizeAssigneeSelection,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/formAssigneeSelection';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  useConfirmDialog,
  useNotify,
  usePrompt,
} from 'core/hooks';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useUpdateForm,
} from 'views/EMR/Settings/hooks';
import {
  FormBuilderResponseDTO,
  UpdateFormRequestDTO,
  FormCategoryDTO,
} from 'dtos';
import {
  FormFieldAssignee,
} from 'dtos/formFieldAssignee';
import isConsentFormCategory from 'views/EMR/Settings/Forms/utils';
import ToolTip from 'core/components/Tooltip';
import {
  FileViewerContext,
} from 'pages/Dashboard/contexts/fileViewer';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import {
  getErrorMessage,
} from 'utils/errors';

type FormMetaSnapshot = {
  title: string;
  category: FormCategoryDTO | null;
};

type FieldHistoryEntry = {
  fields: PdfTemplateField[];
  focusFieldId: string | null;
  selectedFieldId: string | null;
  selectedFieldIds: string[];
};

type FieldHistoryState = {
  entries: FieldHistoryEntry[];
  index: number;
};

type FieldHistoryTransaction = {
  before: FieldHistoryEntry;
  commitRequested: boolean;
  focusFieldId: string | null;
  mode: 'deferred' | 'immediate';
};

const unsavedChangesPromptTitle = 'Leave Form?';
const unsavedChangesPromptMessage = (
  <div className="!mt-0">
    You have unsaved changes. Do you want to leave without saving?
  </div>
);

type Props = {
  formId: number;
  builderState?: PdfTemplateBuilderLocationState | null;
  onClose: () => void;
};

function BuilderSkeleton() {
  return (
    <BuilderLayout
      topBar={(
        <div className="border-b border-gray-200 bg-white p-4">
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0 flex-1">
              <Skeleton variant="text" className="!h-7 !w-72 !rounded-md" />
              <Skeleton variant="rectangular" className="!mt-3 !h-9 !w-56 !rounded-md" />
            </div>
            <div className="flex items-center gap-3">
              <Skeleton variant="rectangular" className="!h-10 !w-28 !rounded-md" />
              <Skeleton variant="rectangular" className="!h-10 !w-32 !rounded-md" />
              <Skeleton variant="circular" className="!h-8 !w-8" />
              <Skeleton variant="circular" className="!h-8 !w-8" />
            </div>
          </div>
          <div className="mt-4 border-t border-gray-100 pt-3">
            <div className="flex items-center gap-2">
              <Skeleton variant="text" className="!h-4 !w-14 !rounded-md" />
              <Skeleton variant="text" className="!h-4 !w-12 !rounded-md" />
            </div>
          </div>
        </div>
      )}
      documentViewer={(
        <div className="flex h-full min-h-[640px] items-start justify-center bg-gray-100 p-8">
          <Skeleton variant="rectangular" className="!h-[720px] !w-full !max-w-[820px] !rounded-lg" />
        </div>
      )}
      propertiesPanel={(
        <div className="flex h-full flex-col gap-4 bg-white p-5">
          <Skeleton variant="text" className="!h-5 !w-28 !rounded-md" />
          <Skeleton variant="rectangular" className="!h-11 !w-full !rounded-md" />
          <Skeleton variant="text" className="!h-5 !w-24 !rounded-md" />
          <Skeleton variant="rectangular" className="!h-11 !w-full !rounded-md" />
          <Skeleton variant="text" className="!mt-2 !h-5 !w-24 !rounded-md" />
          <Skeleton variant="rectangular" className="!h-28 !w-full !rounded-lg" />
          <Skeleton variant="rectangular" className="!h-10 !w-40 !rounded-md" />
          <Skeleton variant="rectangular" className="!h-10 !w-36 !rounded-md" />
        </div>
      )}
    />
  );
}

export default function PdfTemplateBuilderDialog({
  formId,
  builderState = null,
  onClose,
}: Props) {
  const notify = useNotify();
  const { openDialog } = useContext(FileViewerContext);
  const { confirm, closeConfirm } = useConfirmDialog();
  const hasInitializedFormMeta = useRef(false);
  const copiedFieldRef = useRef<PdfTemplateField[]>([]);
  const pendingFieldHistoryTransactionRef = useRef<FieldHistoryTransaction | null>(null);
  const pendingFieldHistoryCommitTimeoutRef = useRef<number | null>(null);
  const formIdString = String(formId);
  const {
    data: formTemplateSettings,
    isLoading: isFormTemplateSettingsLoading,
  } = useGetFormTemplateSettings();
  const {
    data,
    isLoading,
    refetch: refetchFormBuilder,
  } = useGetFormBuilder(formId);
  const { mutateAsync: saveFormFields, isPending: isSaving } = (
    useSaveFormFields()
  );
  const { mutateAsync: updateForm, isPending: isUpdatingForm } = (
    useUpdateForm()
  );
  const { mutateAsync: deleteForm, isPending: isDeletingForm } = (
    useDeleteForm()
  );
  const { mutateAsync: replaceFormPdf, isPending: isReplacingDocument } = (
    useReplaceFormPdf()
  );
  const { mutateAsync: getFormPreviewPdf, isPending: isPreviewing } = (
    useGetFormPreviewPdf()
  );

  const [formBuilderConfig, setFormBuilderConfig] = useState<FormBuilderResponseDTO | null>(null);
  const [formCategory, setFormCategory] = useState<FormCategoryDTO | null>(null);
  const {
    data: categoryRulesResponse,
  } = useGetFormCategoryRules();
  const categoryRule = useMemo(
    () => findFormCategoryRule(categoryRulesResponse?.rules, formCategory),
    [categoryRulesResponse?.rules, formCategory],
  );
  const [selectedFormAssignees, setSelectedFormAssignees] = useState<FormFieldAssignee[]>([
    FormFieldAssignee.Patient,
  ]);
  const isMixedAssigneeMode = useMemo(
    () => getIsMixedAssigneeMode(selectedFormAssignees),
    [selectedFormAssignees],
  );
  const defaultAssigneeForNewField = useMemo(
    () => getDefaultAssigneeForNewField(selectedFormAssignees, categoryRule),
    [categoryRule, selectedFormAssignees],
  );
  const [documentPageCount, setDocumentPageCount] = useState<number | null>(null);
  const [localDocumentOverride, setLocalDocumentOverride] = useState<(PdfTemplateBuilderLocationState['document']) | null>(
    builderState?.document?.isObjectUrl
      ? builderState?.document ?? null
      : null);
  const [isAwaitingRadioGroupOption, setIsAwaitingRadioGroupOption] = useState(false);
  const [isAwaitingCheckboxGroupOption, setIsAwaitingCheckboxGroupOption] = useState(false);
  const [fieldHistoryState, setFieldHistoryState] = useState<FieldHistoryState>({
    entries: [],
    index: 0,
  });
  const fieldHistoryStateRef = useRef<FieldHistoryState>({
    entries: [],
    index: 0,
  });
  const builderSnapshotRef = useRef({
    fields: [] as PdfTemplateField[],
    selectedFieldId: null as string | null,
    selectedFieldIds: [] as string[],
  });
  const keyboardSelectionRef = useRef({
    fields: [] as PdfTemplateField[],
    selectedFieldId: null as string | null,
    selectedFieldIds: [] as string[],
  });
  const resolvedFormBuilderConfig = formBuilderConfig ?? data ?? null;
  const formPdf = head(resolvedFormBuilderConfig?.formPdf?.list ?? []);

  const apiDocument = useMemo(() => (
    formPdf?.url?.value ? {
      fileName: formPdf.fileName ?? 'consent-form.pdf',
      url: formPdf.url.value,
    } : null
  ), [formPdf?.fileName, formPdf?.url?.value]);

  const document = useMemo(() => (
    localDocumentOverride ?? apiDocument ?? builderState?.document ?? {
      fileName: '',
      url: '',
    }
  ), [apiDocument, builderState, localDocumentOverride]);

  const activePages = localDocumentOverride?.isObjectUrl
    ? null
    : (resolvedFormBuilderConfig?.pages ?? null);

  const formIsDraft = resolvedFormBuilderConfig?.isDraft !== false;
  const formPageCount = documentPageCount
    ?? activePages?.length
    ?? 1;

  const fallbackFormSummary: Partial<PdfTemplateSummary> | undefined = apiDocument ? {
    id: formIdString,
    title: apiDocument.fileName,
    status: 'draft',
    isDraft: true,
    updatedAt: 'Draft in progress',
    pageCount: 1,
  } : undefined;

  const formSummary = useMemo<Partial<PdfTemplateSummary> | undefined>(() => (
    resolvedFormBuilderConfig?.title ? {
      id: formIdString,
      title: resolvedFormBuilderConfig.title,
      status: formIsDraft ? 'draft' : 'published',
      isDraft: formIsDraft,
      updatedAt: formIsDraft ? 'Draft in progress' : 'Published',
      pageCount: formPageCount,
      createdOn: resolvedFormBuilderConfig.createdOn,
      updatedOn: resolvedFormBuilderConfig.updatedOn,
      createdByName:
        getDisplayName(resolvedFormBuilderConfig.createdByUser?.formattedNames, undefined),
      updatedByName:
        getDisplayName(resolvedFormBuilderConfig.updatedByUser?.formattedNames, undefined),
    } : (
      builderState?.template ?? fallbackFormSummary
    )
  ), [
    apiDocument,
    builderState,
    fallbackFormSummary,
    formIdString,
    resolvedFormBuilderConfig?.createdOn,
    resolvedFormBuilderConfig?.createdByUser?.formattedNames?.indexByLastNameWithCredentials,
    resolvedFormBuilderConfig?.title,
    resolvedFormBuilderConfig?.updatedByUser?.formattedNames?.indexByLastNameWithCredentials,
    resolvedFormBuilderConfig?.updatedOn,
    formPageCount,
    formIsDraft,
  ]);
  const formFields = useMemo(
    () => (resolvedFormBuilderConfig
      ? normalizeFormFields(resolvedFormBuilderConfig.fields)
      : undefined),
    [resolvedFormBuilderConfig],
  );
  const {
    state,
    selectedField,
    addField,
    removeField,
    selectField,
    updateFieldRect,
    updateFieldPosition,
    updateSelectedFieldAlias,
    updateSelectedFieldHorizontalAlignment,
    updateSelectedFieldVerticalAlignment,
    updateSelectedFieldFontSize,
    updateSelectedFieldAssignee,
    updateSelectedFieldRequired,
    coerceFieldAssignees,
    updateSelectedFieldName,
    updateSelectedRadioGroupName,
    updateSelectedCheckboxGroupName,
    updateSelectedFieldType,
    addRadioGroupOption,
    addCheckboxGroupOption,
    updateFormTitle,
    duplicateFields,
    restoreHistorySnapshot,
    removeFields,
    setZoom,
  } = useFormBuilderState(
    formIdString,
    document,
    formSummary,
    formFields,
  );
  const currentFormMeta = {
    title: state.template.title,
    category: formCategory,
  };
  const persistedFormMeta = {
    title: resolvedFormBuilderConfig?.title
      ?? builderState?.template?.title
      ?? fallbackFormSummary?.title
      ?? '',
    category: resolvedFormBuilderConfig?.category ?? null,
  };
  const serializedFormFields = useMemo(
    () => serializeFormFields(state.fields),
    [state.fields],
  );
  const serializedPersistedFormFields = useMemo(
    () => serializeFormFields(formFields ?? []),
    [formFields],
  );
  const defaultFieldType = getDefaultFieldType(formTemplateSettings?.fieldTypes);
  const isBuilderLoading = isFormTemplateSettingsLoading || isLoading;
  const hasUnsavedFieldChanges = !isEqual(
    serializedFormFields,
    serializedPersistedFormFields,
  );
  const hasUnsavedFormMetaChanges = currentFormMeta.title !== persistedFormMeta.title
    || currentFormMeta.category?.value !== persistedFormMeta.category?.value;
  const hasUnsavedChanges = hasUnsavedFieldChanges || hasUnsavedFormMetaChanges;
  const createFieldHistoryEntry = (
    fields: PdfTemplateField[] = builderSnapshotRef.current.fields,
    selectedFieldId: string | null = builderSnapshotRef.current.selectedFieldId,
    selectedFieldIds: string[] = builderSnapshotRef.current.selectedFieldIds,
    focusFieldId?: string | null,
  ): FieldHistoryEntry => ({
    fields,
    selectedFieldId,
    selectedFieldIds,
    focusFieldId: focusFieldId
      ?? selectedFieldId
      ?? selectedFieldIds.at(-1)
      ?? fields.at(-1)?.id
      ?? null,
  });
  const updateBuilderSnapshotRef = (
    fields: PdfTemplateField[],
    selectedFieldId: string | null,
    selectedFieldIds: string[],
  ) => {
    builderSnapshotRef.current = {
      fields,
      selectedFieldId,
      selectedFieldIds,
    };
  };
  const clearPendingFieldHistoryCommitTimeout = () => {
    if (pendingFieldHistoryCommitTimeoutRef.current !== null) {
      window.clearTimeout(pendingFieldHistoryCommitTimeoutRef.current);
      pendingFieldHistoryCommitTimeoutRef.current = null;
    }
  };
  const resetFieldHistoryState = () => {
    pendingFieldHistoryTransactionRef.current = null;
    clearPendingFieldHistoryCommitTimeout();
    const nextFieldHistoryState = {
      entries: [],
      index: 0,
    };
    fieldHistoryStateRef.current = nextFieldHistoryState;
    setFieldHistoryState(nextFieldHistoryState);
  };
  const beginFieldHistoryTransaction = (
    focusFieldId?: string | null,
    mode: FieldHistoryTransaction['mode'] = 'immediate',
  ) => {
    if (pendingFieldHistoryTransactionRef.current) {
      return;
    }

    pendingFieldHistoryTransactionRef.current = {
      before: createFieldHistoryEntry(
        builderSnapshotRef.current.fields,
        builderSnapshotRef.current.selectedFieldId,
        builderSnapshotRef.current.selectedFieldIds,
        focusFieldId,
      ),
      commitRequested: false,
      focusFieldId: focusFieldId
        ?? builderSnapshotRef.current.selectedFieldId
        ?? builderSnapshotRef.current.selectedFieldIds.at(-1)
        ?? builderSnapshotRef.current.fields.at(-1)?.id
        ?? null,
      mode,
    };
  };
  const requestFieldHistoryCommit = (focusFieldId?: string | null) => {
    const pendingFieldHistoryTransaction = pendingFieldHistoryTransactionRef.current;

    if (!pendingFieldHistoryTransaction) {
      return;
    }

    if (typeof focusFieldId !== 'undefined') {
      pendingFieldHistoryTransaction.focusFieldId = focusFieldId;
    }

    pendingFieldHistoryTransaction.commitRequested = true;
  };
  const resolveCommittedFieldHistoryState = (
    currentFieldHistoryState: FieldHistoryState,
  ): FieldHistoryState => {
    const pendingFieldHistoryTransaction = pendingFieldHistoryTransactionRef.current;

    if (!pendingFieldHistoryTransaction?.commitRequested) {
      return currentFieldHistoryState;
    }

    clearPendingFieldHistoryCommitTimeout();
    pendingFieldHistoryTransactionRef.current = null;

    const nextEntry = createFieldHistoryEntry(
      builderSnapshotRef.current.fields,
      builderSnapshotRef.current.selectedFieldId,
      builderSnapshotRef.current.selectedFieldIds,
      pendingFieldHistoryTransaction.focusFieldId,
    );

    if (isEqual(pendingFieldHistoryTransaction.before, nextEntry)) {
      return currentFieldHistoryState;
    }

    let entries = currentFieldHistoryState.entries.slice(
      0,
      currentFieldHistoryState.index + 1,
    );

    if (
      entries.length === 0
      || !isEqual(entries.at(-1), pendingFieldHistoryTransaction.before)
    ) {
      entries = [...entries, pendingFieldHistoryTransaction.before];
    }

    return {
      entries: [...entries, nextEntry],
      index: entries.length,
    };
  };
  const commitPendingFieldHistoryTransactionRef = useRef<() => void>(() => {});
  const commitPendingFieldHistoryTransaction = () => {
    const nextFieldHistoryState = resolveCommittedFieldHistoryState(fieldHistoryStateRef.current);
    fieldHistoryStateRef.current = nextFieldHistoryState;
    setFieldHistoryState(nextFieldHistoryState);
  };
  commitPendingFieldHistoryTransactionRef.current = commitPendingFieldHistoryTransaction;
  const appendFieldHistoryEntry = (
    previousEntry: FieldHistoryEntry,
    nextEntry: FieldHistoryEntry,
  ) => {
    const committedFieldHistoryState = resolveCommittedFieldHistoryState(fieldHistoryStateRef.current);
    let entries = committedFieldHistoryState.entries.slice(
      0,
      committedFieldHistoryState.index + 1,
    );

    if (entries.length === 0 || !isEqual(entries.at(-1), previousEntry)) {
      entries = [...entries, previousEntry];
    }

    if (isEqual(entries.at(-1), nextEntry)) {
      const nextFieldHistoryState = {
        entries,
        index: Math.max(entries.length - 1, 0),
      };
      fieldHistoryStateRef.current = nextFieldHistoryState;
      setFieldHistoryState(nextFieldHistoryState);
      return;
    }

    const nextFieldHistoryState = {
      entries: [...entries, nextEntry],
      index: entries.length,
    };
    fieldHistoryStateRef.current = nextFieldHistoryState;
    setFieldHistoryState(nextFieldHistoryState);
  };
  const scheduleDeferredFieldHistoryCommit = (focusFieldId?: string | null) => {
    requestFieldHistoryCommit(focusFieldId);

    clearPendingFieldHistoryCommitTimeout();

    pendingFieldHistoryCommitTimeoutRef.current = window.setTimeout(() => {
      pendingFieldHistoryCommitTimeoutRef.current = null;
      commitPendingFieldHistoryTransactionRef.current();
    }, 120);
  };
  const trackImmediateFieldChange = <Result,>(
    onChange: () => Result,
    focusFieldId?: string | null,
  ): Result => {
    beginFieldHistoryTransaction(focusFieldId);
    const result = onChange();
    requestFieldHistoryCommit(focusFieldId);
    return result;
  };
  const trackSelectedFieldChange = (onChange: () => void) => {
    const focusFieldId = selectedField?.id ?? state.selectedFieldId;

    if (!focusFieldId) {
      onChange();
      return;
    }

    trackImmediateFieldChange(onChange, focusFieldId);
  };
  const canUndoFieldChange = fieldHistoryState.index > 0;
  const canRedoFieldChange = fieldHistoryState.entries.length > 0
    && fieldHistoryState.index < fieldHistoryState.entries.length - 1;
  const visibleFieldHistory = fieldHistoryState.entries[fieldHistoryState.index] ?? null;
  const fieldHistoryLabel = useMemo(() => {
    const historyFieldId = visibleFieldHistory?.focusFieldId ?? state.selectedFieldId;
    const historyField = state.fields.find((field) => field.id === historyFieldId)
      ?? visibleFieldHistory?.fields.find((field) => field.id === historyFieldId)
      ?? visibleFieldHistory?.fields[0];

    if (isNil(historyField)) {
      return null;
    }

    return historyField.type === 'radioGroup' || historyField.type === 'checkboxGroup'
      ? (historyField.groupName ?? historyField.name)
      : historyField.name;
  }, [state.fields, state.selectedFieldId, visibleFieldHistory]);

  usePrompt(hasUnsavedChanges, {
    title: unsavedChangesPromptTitle,
    message: unsavedChangesPromptMessage,
  });

  useEffect(() => {
    if (!categoryRule) {
      return;
    }

    setSelectedFormAssignees(
      inferFormAssigneeSelection(formFields ?? [], categoryRule),
    );
  }, [categoryRule, formFields]);

  useEffect(() => {
    setFormBuilderConfig(data ?? null);
  }, [data, formId]);

  useEffect(() => {
    hasInitializedFormMeta.current = false;
    setFormCategory(null);
    setDocumentPageCount(null);
    setLocalDocumentOverride(
      builderState?.document?.isObjectUrl
        ? builderState?.document ?? null
        : null,
    );
    setIsAwaitingRadioGroupOption(false);
    setIsAwaitingCheckboxGroupOption(false);
    resetFieldHistoryState();
  }, [builderState?.document?.isObjectUrl, builderState?.document?.url, formId]);

  useEffect(() => {
    if (resolvedFormBuilderConfig && !hasInitializedFormMeta.current) {
      setFormCategory(resolvedFormBuilderConfig.category ?? null);
      hasInitializedFormMeta.current = true;
    }
  }, [resolvedFormBuilderConfig]);

  useEffect(() => {
    setDocumentPageCount(activePages?.length ?? null);
  }, [activePages]);

  useEffect(() => {
    const unsubscribe = settingsEventChannel.on('onConsentFormTemplateChange', (params) => {
      if (params.formId !== formId) {
        return;
      }

      if (params.template) {
        setFormBuilderConfig((currentFormBuilderConfig) => ({
          ...(currentFormBuilderConfig ?? data ?? {}),
          ...params.template,
        }));
      }

      if (typeof params.document !== 'undefined') {
        setDocumentPageCount(null);
        setLocalDocumentOverride(params.document);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [formId, data]);

  useEffect(() => {
    const objectUrl = localDocumentOverride?.isObjectUrl
      ? localDocumentOverride.url
      : null;

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [localDocumentOverride]);

  useEffect(() => {
    if (selectedField?.type !== 'radioGroup' && isAwaitingRadioGroupOption) {
      setIsAwaitingRadioGroupOption(false);
    }
    if (selectedField?.type !== 'checkboxGroup' && isAwaitingCheckboxGroupOption) {
      setIsAwaitingCheckboxGroupOption(false);
    }
  }, [isAwaitingRadioGroupOption, isAwaitingCheckboxGroupOption, selectedField?.type]);

  useEffect(() => {
    if (fieldHistoryState.entries.length > 0 || isBuilderLoading) {
      return;
    }

    if (formFields && !isEqual(state.fields, formFields)) {
      return;
    }

    const nextFieldHistoryState = {
      entries: [createFieldHistoryEntry()],
      index: 0,
    };
    fieldHistoryStateRef.current = nextFieldHistoryState;
    setFieldHistoryState(nextFieldHistoryState);
  }, [
    createFieldHistoryEntry,
    fieldHistoryState.entries.length,
    formFields,
    isBuilderLoading,
    state.fields,
  ]);

  useEffect(() => {
    const pendingFieldHistoryTransaction = pendingFieldHistoryTransactionRef.current;

    if (!pendingFieldHistoryTransaction?.commitRequested
      || pendingFieldHistoryTransaction.mode !== 'immediate') {
      return;
    }

    commitPendingFieldHistoryTransaction();
  }, [
    commitPendingFieldHistoryTransaction,
    state.fields,
    state.selectedFieldId,
    state.selectedFieldIds,
  ]);

  const saveForm = async (isDraft: boolean, successMessage: string) => {
    await saveFormFields({
      formId,
      fields: serializedFormFields,
      isDraft,
    });
    settingsEventChannel.emit('onConsentFormTemplateChange', {
      formId,
      template: {
        fields: serializedFormFields,
        isDraft,
      },
    });
    settingsEventChannel.emit('onChangeTemplates');
    notify({
      variant: 'success',
      message: successMessage,
    });
  };

  const saveFormFieldsIfNeeded = async () => {
    if (!isEqual(serializedFormFields, serializedPersistedFormFields)) {
      await saveFormFields({
        formId,
        fields: serializedFormFields,
        isDraft: state.template.isDraft,
      });
      settingsEventChannel.emit('onConsentFormTemplateChange', {
        formId,
        template: {
          fields: serializedFormFields,
          isDraft: state.template.isDraft,
        },
      });
      settingsEventChannel.emit('onChangeTemplates');
    }
  };

  const persistCurrentFormFields = async () => {
    await saveFormFields({
      formId,
      fields: serializedFormFields,
      isDraft: state.template.isDraft,
    });
    settingsEventChannel.emit('onConsentFormTemplateChange', {
      formId,
      template: {
        fields: serializedFormFields,
        isDraft: state.template.isDraft,
      },
    });
    settingsEventChannel.emit('onChangeTemplates');
  };

  const handleSave = async () => {
    const nextIsDraft = state.template.isDraft;

    if (nextIsDraft) {
      await saveForm(nextIsDraft, 'Form saved successfully');
    }
  };

  const handlePublish = async () => {
    await saveForm(false, 'Form published successfully');
    onClose();
  };

  const handleArchive = () => {
    confirm({
      title: 'Archive Form',
      onConfirm: async () => {
        try {
          await deleteForm({ id: formId });
          notify({
            variant: 'success',
            message: 'Form was archived successfully',
          });
          settingsEventChannel.emit('onChangeTemplates');
          onClose();
        } finally {
          closeConfirm();
        }
      },
      content: (
        <p className="mb-4">
          Are you sure you want to archive this form?
        </p>
      ),
    });
  };

  const handleClose = () => {
    if (!hasUnsavedChanges) {
      onClose();
      return;
    }

    confirm({
      className: '!w-[32rem]',
      disableBackdropClick: true,
      hideCloseBtn: true,
      title: unsavedChangesPromptTitle,
      content: unsavedChangesPromptMessage,
      confirmText: 'Stay',
      cancelText: 'Leave',
      onConfirm: () => {
        closeConfirm();
      },
      onClose: () => {
        onClose();
        closeConfirm();
      },
    });
  };

  const buildUpdateFormRequest = (
    nextMeta: FormMetaSnapshot,
  ): UpdateFormRequestDTO => {
    const request: UpdateFormRequestDTO = {
      formId,
      title: nextMeta.title,
    };

    if (nextMeta.category && isConsentFormCategory(nextMeta.category.value)) {
      request.category = nextMeta.category.value as UpdateFormRequestDTO['category'];
    }

    return request;
  };

  const updateFormMeta = async (nextMeta: FormMetaSnapshot, successMessage: string) => {
    const previousMeta = currentFormMeta;
    if (previousMeta.title === nextMeta.title
      && previousMeta.category?.value === nextMeta.category?.value) {
      return;
    }

    updateFormTitle(nextMeta.title);
    setFormCategory(nextMeta.category);

    try {
      await saveFormFieldsIfNeeded();
      const response = await updateForm(
        buildUpdateFormRequest(nextMeta),
      );
      const nextTitle = response.title ?? nextMeta.title;
      const nextCategory = response.category ?? nextMeta.category;

      updateFormTitle(nextTitle);
      setFormCategory(nextCategory ?? null);
      settingsEventChannel.emit('onConsentFormTemplateChange', {
        formId,
        template: {
          title: nextTitle,
          category: nextCategory ?? undefined,
        },
      });
      settingsEventChannel.emit('onChangeTemplates');
      notify({
        variant: 'success',
        message: successMessage,
      });
    } catch (error) {
      updateFormTitle(previousMeta.title);
      setFormCategory(previousMeta.category);
      throw error;
    }
  };

  const handleUpdateFormTitle = async (title: string) => {
    await updateFormMeta({
      title,
      category: currentFormMeta.category,
    }, 'Form name updated successfully');
  };

  const handleUpdateFormCategory = async (category: FormCategoryDTO) => {
    const nextCategoryRule = findFormCategoryRule(categoryRulesResponse?.rules, category);
    if (nextCategoryRule && (nextCategoryRule.allowedAssignees?.length ?? 0) <= 1) {
      const nextAssignee = nextCategoryRule.defaultAssigneeForNewField ?? FormFieldAssignee.Patient;
      coerceFieldAssignees(nextAssignee);
      setSelectedFormAssignees(normalizeAssigneeSelection([nextAssignee], nextCategoryRule));
    } else if (nextCategoryRule) {
      setSelectedFormAssignees(
        inferFormAssigneeSelection(state.fields, nextCategoryRule),
      );
    }
    await updateFormMeta({
      title: currentFormMeta.title,
      category,
    }, 'Form category updated successfully');
  };

  const handleFormAssigneeSelectionChange = (nextSelection: FormFieldAssignee[]) => {
    const affectedCount = countFieldsNeedingCoercion(state.fields, nextSelection);

    if (affectedCount === 0) {
      setSelectedFormAssignees(nextSelection);
      return;
    }

    const targetAssignee = getCoercionTargetAssignee(nextSelection);
    const assigneeLabel = targetAssignee ? getAssigneeLabel(targetAssignee) : '';

    confirm({
      title: 'Change assignee for all fields?',
      content: (
        <p className="mb-4">
          Switching to
          {' '}
          <strong>
            {assigneeLabel}
            {' '}
            only
          </strong>
          {' '}
          will update
          {' '}
          <strong>
            {affectedCount}
          </strong>
          {' '}
          field
          {affectedCount === 1 ? '' : 's'}
          {' '}
          currently assigned to a different assignee.
        </p>
      ),
      onConfirm: () => {
        if (targetAssignee) {
          coerceFieldAssignees(targetAssignee);
        }
        setSelectedFormAssignees(nextSelection);
        closeConfirm();
      },
      onClose: closeConfirm,
    });
  };
  const handleRemoveField = (fieldId: string) => {
    trackImmediateFieldChange(() => {
      removeField(fieldId);
    }, fieldId);
  };

  const handleRemoveFields = (fieldIds: string[]) => {
    trackImmediateFieldChange(() => {
      removeFields(fieldIds);
    }, fieldIds.at(-1) ?? null);
  };

  const handlePasteFields = () => {
    const duplicatedFields = trackImmediateFieldChange(
      () => duplicateFields(copiedFieldRef.current),
      copiedFieldRef.current.at(-1)?.id ?? null,
    );
    copiedFieldRef.current = duplicatedFields;
  };

  const handleSelectField = (
    fieldId: string | null,
    options?: { multi?: boolean },
  ) => {
    selectField(fieldId, options);
  };

  const commitAddedFieldHistory = (addedField: PdfTemplateField) => {
    const previousEntry = createFieldHistoryEntry(
      state.fields,
      state.selectedFieldId,
      state.selectedFieldIds,
    );
    const nextEntry = createFieldHistoryEntry(
      [...state.fields, addedField],
      addedField.id,
      [addedField.id],
      addedField.id,
    );

    updateBuilderSnapshotRef(
      nextEntry.fields,
      nextEntry.selectedFieldId,
      nextEntry.selectedFieldIds,
    );
    appendFieldHistoryEntry(previousEntry, nextEntry);
  };

  const handleFieldRectChange = (fieldId: string, rect: PdfPageRect) => {
    beginFieldHistoryTransaction(fieldId, 'deferred');
    updateFieldRect(fieldId, rect);
    scheduleDeferredFieldHistoryCommit(fieldId);
  };

  const handleFieldPositionChange = (
    fieldId: string,
    pageNumber: number,
    rect: PdfPageRect,
  ) => {
    beginFieldHistoryTransaction(fieldId, 'deferred');
    updateFieldPosition(fieldId, pageNumber, rect);
    scheduleDeferredFieldHistoryCommit(fieldId);
  };

  const handleFieldTransformStart = (fieldId: string) => {
    beginFieldHistoryTransaction(fieldId, 'deferred');
  };

  const handleFieldTransformEnd = (fieldId: string) => {
    scheduleDeferredFieldHistoryCommit(fieldId);
  };

  const restoreFieldHistoryEntry = (entry: FieldHistoryEntry) => {
    pendingFieldHistoryTransactionRef.current = null;
    clearPendingFieldHistoryCommitTimeout();

    restoreHistorySnapshot(
      entry.fields,
      entry.selectedFieldId,
      entry.selectedFieldIds,
    );
    updateBuilderSnapshotRef(
      entry.fields,
      entry.selectedFieldId,
      entry.selectedFieldIds,
    );
  };

  function handleUndoFieldChange() {
    const committedFieldHistoryState = resolveCommittedFieldHistoryState(
      fieldHistoryStateRef.current,
    );

    if (committedFieldHistoryState.index === 0) {
      fieldHistoryStateRef.current = committedFieldHistoryState;
      setFieldHistoryState(committedFieldHistoryState);
      return;
    }

    const nextFieldHistoryState = {
      ...committedFieldHistoryState,
      index: committedFieldHistoryState.index - 1,
    };
    const historyEntryToRestore = committedFieldHistoryState.entries[
      committedFieldHistoryState.index - 1
    ];

    fieldHistoryStateRef.current = nextFieldHistoryState;
    setFieldHistoryState(nextFieldHistoryState);
    restoreFieldHistoryEntry(historyEntryToRestore);
  }

  function handleRedoFieldChange() {
    const committedFieldHistoryState = resolveCommittedFieldHistoryState(
      fieldHistoryStateRef.current,
    );

    if (
      committedFieldHistoryState.index
      >= committedFieldHistoryState.entries.length - 1
    ) {
      fieldHistoryStateRef.current = committedFieldHistoryState;
      setFieldHistoryState(committedFieldHistoryState);
      return;
    }

    const nextFieldHistoryState = {
      ...committedFieldHistoryState,
      index: committedFieldHistoryState.index + 1,
    };
    const historyEntryToRestore = committedFieldHistoryState.entries[
      committedFieldHistoryState.index + 1
    ];

    fieldHistoryStateRef.current = nextFieldHistoryState;
    setFieldHistoryState(nextFieldHistoryState);
    restoreFieldHistoryEntry(historyEntryToRestore);
  }

  const handleUndoFieldChangeRef = useRef(handleUndoFieldChange);
  const handleRedoFieldChangeRef = useRef(handleRedoFieldChange);
  const handlePasteFieldsRef = useRef(handlePasteFields);
  const handleRemoveFieldRef = useRef(handleRemoveField);
  const handleRemoveFieldsRef = useRef(handleRemoveFields);

  keyboardSelectionRef.current = {
    fields: state.fields,
    selectedFieldId: state.selectedFieldId,
    selectedFieldIds: state.selectedFieldIds,
  };
  updateBuilderSnapshotRef(
    state.fields,
    state.selectedFieldId,
    state.selectedFieldIds,
  );
  handleUndoFieldChangeRef.current = handleUndoFieldChange;
  handleRedoFieldChangeRef.current = handleRedoFieldChange;
  handlePasteFieldsRef.current = handlePasteFields;
  handleRemoveFieldRef.current = handleRemoveField;
  handleRemoveFieldsRef.current = handleRemoveFields;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const {
        fields,
        selectedFieldId,
        selectedFieldIds,
      } = keyboardSelectionRef.current;
      const historyShortcutAction = getFieldHistoryShortcutAction(event);
      const isCopyShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'c';
      const isPasteShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'v';
      const shouldRemoveField = selectedFieldIds.length > 0
        && (event.key === 'Delete' || event.key === 'Backspace')
        && !isEditableKeyboardTarget(event.target);

      if (historyShortcutAction) {
        event.preventDefault();
        event.stopPropagation();
        if (historyShortcutAction === 'undo') {
          handleUndoFieldChangeRef.current();
          return;
        }

        handleRedoFieldChangeRef.current();
        return;
      }

      if (isEditableKeyboardTarget(event.target)) {
        return;
      }

      if (isCopyShortcut && selectedFieldIds.length > 0) {
        event.preventDefault();
        copiedFieldRef.current = fields.filter((field) => selectedFieldIds.includes(field.id));
        return;
      }

      if (isPasteShortcut && copiedFieldRef.current.length > 0) {
        event.preventDefault();
        handlePasteFieldsRef.current();
        return;
      }

      if (shouldRemoveField) {
        event.preventDefault();
        if (selectedFieldIds.length === 1 && selectedFieldId) {
          handleRemoveFieldRef.current(selectedFieldId);
          return;
        }

        handleRemoveFieldsRef.current(selectedFieldIds);
      }
    };

    window.document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  const handleReplaceDocument = async (file: File) => {
    try {
      const dataUri = await blobToDataUrl(file);

      await replaceFormPdf({
        formId,
        fileName: file.name,
        dataUri,
      });
      const refreshedFormResponse = await refetchFormBuilder();
      const refreshedForm = refreshedFormResponse.data;

      if (refreshedForm) {
        setFormBuilderConfig(refreshedForm);
        setFormCategory(refreshedForm.category ?? null);
        setDocumentPageCount(refreshedForm.pages?.length ?? null);
      }

      setLocalDocumentOverride(null);

      settingsEventChannel.emit('onConsentFormTemplateChange', {
        formId,
        template: refreshedForm ?? undefined,
        document: null,
      });
      settingsEventChannel.emit('onChangeTemplates');

      notify({
        variant: 'success',
        message: 'Form document replaced successfully',
      });
    } catch (error) {
      console.error('Error replacing form document:', error);
    }
  };

  const handlePreview = async () => {
    try {
      await persistCurrentFormFields();
      const preview = await getFormPreviewPdf(formId);
      const media = head(preview?.list ?? []);

      if (isNil(media)) {
        notify({
          variant: 'error',
          message: 'Preview was unavailable',
        });
        return;
      }

      openDialog({
        title: media.fileName ?? state.template.title,
        content: <MediaPreview media={media} />,
      });
    } catch (error) {
      notify({
        variant: 'error',
        message: getErrorMessage(error) ?? 'Preview was unavailable',
      });
    }
  };

  if (isBuilderLoading) {
    return (
      <Dialog
        open
        onClose={handleClose}
        hideCloseBtn
        maxWidth={false}
        fullWidth
        classes={{
          root: '!w-full !h-screen flex flex-col !p-0 !space-y-0 bg-gray-100',
          dialog: 'hidden-backdrop populate-iframe-modal !m-0 settings-form-dialog',
          bodyRoot: '!w-screen !m-0 !max-w-[calc(100%-12px)]',
          container: '!justify-start',
        }}
      >
        <BuilderSkeleton />
      </Dialog>
    );
  }
  if (isEmptyString(document.url ?? '')) {
    return (
      <Dialog
        open
        onClose={handleClose}
        hideCloseBtn
        maxWidth={false}
        fullWidth
        classes={{
          root: '!w-full !h-screen flex flex-col !p-0 !space-y-0 bg-gray-100',
          dialog: 'hidden-backdrop populate-iframe-modal !m-0 settings-form-dialog',
          bodyRoot: '!w-screen !m-0 !max-w-[calc(100%-12px)]',
          container: '!justify-start',
        }}
      >
        <div className="relative flex h-full w-full items-center justify-center bg-gray-100">
          <IconButton
            onClick={handleClose}
            className="!absolute !right-3 !top-3 !h-8 !w-8"
          >
            <CloseIcon className="!h-5 !w-5" />
          </IconButton>
          <div className="text-base font-medium text-gray-500">
            Form not found
          </div>
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog
      open
      onClose={handleClose}
      closeBtnClasses="!hidden"
      title={(
        <TopBar
          formSummary={state.template}
          category={formCategory}
          categoryRule={categoryRule}
          selectedFormAssignees={selectedFormAssignees}
          isSaving={isSaving}
          isUpdatingForm={isUpdatingForm}
          headerActions={(
            <>
              <ToolTip title="Archive Form">
                <span className="inline-flex !w-8 !h-8">
                  <IconButton
                    color="error"
                    onClick={handleArchive}
                    disabled={isDeletingForm}
                    className="!w-8 !h-8"
                  >
                    <DeleteIcon className="!h-6 !w-6 !text-inherit" />
                  </IconButton>
                </span>
              </ToolTip>
              <IconButton
                onClick={handleClose}
                className="!w-8 !h-8"
              >
                <CloseIcon className="!h-6 !w-6" />
              </IconButton>
            </>
          )}
          onUpdateFormTitle={handleUpdateFormTitle}
          onUpdateFormCategory={handleUpdateFormCategory}
          onFormAssigneeSelectionChange={handleFormAssigneeSelectionChange}
          onPreview={handlePreview}
          isPreviewing={isPreviewing}
          onPublish={handlePublish}
          onSave={handleSave}
        />
      )}
      maxWidth={false}
      fullWidth
      classes={{
        root: '!w-full !h-screen flex flex-col !p-0 !space-y-0',
        dialog: 'hidden-backdrop populate-iframe-modal !m-0 settings-form-dialog',
        bodyRoot: '!w-screen !m-0 !max-w-[calc(100%-12px)]',
        container: '!justify-start',
        title: 'border-b border-b-gray-300 p-4',
      }}
    >
      <BuilderLayout
        topBar={null}
        documentViewer={(
          <PdfDocumentViewer
            document={state.document}
            pages={activePages}
            fields={state.fields}
            selectedFieldId={state.selectedFieldId}
            selectedFieldIds={state.selectedFieldIds}
            zoom={state.zoom}
            aliasOptions={formTemplateSettings?.autoFillAliases}
            defaultAssigneeForNewField={defaultAssigneeForNewField}
            onPageCountChange={setDocumentPageCount}
            onFieldDraw={(pageNumber, rect) => {
              if (isAwaitingRadioGroupOption && selectedField?.type === 'radioGroup') {
                const addedField = addRadioGroupOption(rect, pageNumber);
                if (addedField) {
                  commitAddedFieldHistory(addedField);
                }
                setIsAwaitingRadioGroupOption(false);
                return;
              }

              if (isAwaitingCheckboxGroupOption && selectedField?.type === 'checkboxGroup') {
                const addedField = addCheckboxGroupOption(rect, pageNumber);
                if (addedField) {
                  commitAddedFieldHistory(addedField);
                }
                setIsAwaitingCheckboxGroupOption(false);
                return;
              }

              const addedField = addField(
                rect,
                defaultFieldType,
                pageNumber,
                defaultAssigneeForNewField,
              );
              commitAddedFieldHistory(addedField);
            }}
            onFieldRemove={handleRemoveField}
            onFieldRectChange={handleFieldRectChange}
            onFieldPositionChange={handleFieldPositionChange}
            onFieldTransformStart={handleFieldTransformStart}
            onFieldTransformEnd={handleFieldTransformEnd}
            onSelectField={handleSelectField}
            onZoomChange={setZoom}
          />
        )}
        propertiesPanel={(
          <PropertiesPanel
            selectedField={selectedField}
            selectedFieldCount={state.selectedFieldIds.length}
            selectedFieldIds={state.selectedFieldIds}
            fields={state.fields}
            document={state.document}
            aliasOptions={formTemplateSettings?.autoFillAliases}
            fieldTypes={formTemplateSettings?.fieldTypes}
            categoryRule={categoryRule}
            isMixedAssigneeMode={isMixedAssigneeMode}
            defaultAssigneeForNewField={defaultAssigneeForNewField}
            canReplaceDocument
            isReplacingDocument={isReplacingDocument}
            isAwaitingRadioGroupOption={isAwaitingRadioGroupOption}
            isAwaitingCheckboxGroupOption={isAwaitingCheckboxGroupOption}
            fieldHistoryLabel={fieldHistoryLabel}
            canUndoFieldChange={canUndoFieldChange}
            canRedoFieldChange={canRedoFieldChange}
            onFieldAliasChange={(aliasId) => {
              trackSelectedFieldChange(() => {
                updateSelectedFieldAlias(aliasId);
              });
            }}
            onFieldVerticalAlignmentChange={(alignment) => {
              trackSelectedFieldChange(() => {
                updateSelectedFieldVerticalAlignment(alignment);
              });
            }}
            onFieldHorizontalAlignmentChange={(alignment) => {
              trackSelectedFieldChange(() => {
                updateSelectedFieldHorizontalAlignment(alignment);
              });
            }}
            onFieldFontSizeChange={(fontSize) => {
              trackSelectedFieldChange(() => {
                updateSelectedFieldFontSize(fontSize);
              });
            }}
            onFieldAssigneeChange={(assignee) => {
              trackSelectedFieldChange(() => {
                updateSelectedFieldAssignee(assignee);
              });
            }}
            onFieldRequiredChange={(isRequired) => {
              trackSelectedFieldChange(() => {
                updateSelectedFieldRequired(isRequired);
              });
            }}
            onFieldNameChange={(fieldName) => {
              trackSelectedFieldChange(() => {
                updateSelectedFieldName(fieldName);
              });
            }}
            onRadioGroupNameChange={(groupName) => {
              trackSelectedFieldChange(() => {
                updateSelectedRadioGroupName(groupName);
              });
            }}
            onCheckboxGroupNameChange={(groupName) => {
              trackSelectedFieldChange(() => {
                updateSelectedCheckboxGroupName(groupName);
              });
            }}
            onFieldTypeChange={(fieldType) => {
              trackSelectedFieldChange(() => {
                updateSelectedFieldType(fieldType);
              });
            }}
            onAddRadioGroupOption={() => {
              setIsAwaitingRadioGroupOption(true);
            }}
            onAddCheckboxGroupOption={() => {
              setIsAwaitingCheckboxGroupOption(true);
            }}
            onSelectField={handleSelectField}
            onClearSelection={() => {
              setIsAwaitingRadioGroupOption(false);
              setIsAwaitingCheckboxGroupOption(false);
              handleSelectField(null);
            }}
            onUndoFieldChange={handleUndoFieldChange}
            onRedoFieldChange={handleRedoFieldChange}
            onReplaceDocument={handleReplaceDocument}
          />
        )}
      />
    </Dialog>
  );
}
